/* React and Next */
import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

/* Material UI */
import {
  Grid, darken, Divider, lighten,
} from '@material-ui/core';

import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';

/* Local imports */
import { Referral } from 'typings/auth/login';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Link from '@/components/links/Link';
// import {
//   Appbar, FormPassword, EmailValidator, SocialNetwork, Footer,
// } from '@/features/auth/';
import withAuthTry from '@/hoc/withAuthTry';
import RootLayout from '@/components/layout/RootLayout';
import Appbar from '@/features/auth/components/AppBar';
import Footer from '@/features/auth/components/Footer';
import OAuthLogin from '@/features/auth/components/OAuthLogin';
import FormPassword from '@/features/auth/login/PasswordStep';
import EmailValidator from '@/features/auth/components/Email';
import withQueryParams from '@/hoc/withQueryParams';
import Typography from '@/features/markets/components/Typography';
import Spacer from '@/components/ui-components/Spacer';
import global from '../../.global.json';

/* Styles */
const useStyles = makeStyles((theme) => ({
  heading: {
    textAlign: 'center',
    fontSize: '48px',
    fontWeight: 400,
    marginBottom: theme.spacing(3),
  },
  gridPadding: {
    padding: theme.spacing(1),
  },
  spacer: {
    minHeight: theme.spacing(2),
  },
  wrapper: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    backgroundColor: lighten(theme.palette.secondary.main, 0.8),
  },
  textField: {
    marginBottom: theme.spacing(1),
  },
  container: {
    minHeight: 'calc(100vh - 67px - 36px )',
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.primary.dark,
  },
  forgotPasswordWrapper: {
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  socialNetworksText: {
    color: darken(theme.palette.medium.main, 0.2),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  link: {
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
}));

/* LoginPage function */
const LoginPage = () => {
  const classes = useStyles();
  const { query, push } = useRouter();
  const { t } = useTranslation('users');
  const [email, setEmail] = useState((query.email as string) || '');

  // are we showing the initial form with only the email field
  // or the password form, too?
  const [showForm, setShowForm] = useState<boolean>(false);

  // is the user coming from the signup page or not
  const [referral, setReferral] = useState<Referral | null>(
    query.referral as Referral,
  );

  // Show an alert if the user is trying to login with a SNS account
  const [social, setSocial] = useState<boolean | null>(!!query.social);

  return (
    <>
      {/* Imported appbar */}
      <Appbar />
      {/* Login content */}
      <RootLayout containerized disableAppbarAndFooter>
        <Grid
          container
          spacing={1}
          direction="row"
          justify="center"
          alignItems="center"
          alignContent="center"
          className={classes.container}
        >
          {/* Login title */}
          <Grid item xs={12} md={4}>
            <Grid container justify="flex-start">
              <Typography
                variant="header"
                color="primary"
                className={classes.heading}
              >
                {t('login.login')}
              </Typography>
            </Grid>
            <Spacer spacing={3} />
            {referral // Tried to signup/reset with an existing account//Social signup
              && !showForm && (
                <Alert
                  severity={referral === 'social' ? 'warning' : 'info'}
                  className={classes.alert}
                  onClose={() => setReferral(null)}
                >
                  {t(`login.referral.${referral}` as const)}
                </Alert>
            )}
            {social && (
              <Alert
                severity="warning"
                className={classes.alert}
                onClose={() => setSocial(null)}
              >
                {t('login.socialLoginWarning')}
              </Alert>
            )}
            <EmailValidator
              onComplete={(formEmail) => {
                setShowForm(true);
                setSocial(false);
                setEmail(formEmail);
              }}
              onCancel={() => setShowForm(false)}
              onError={(formEmail) => push(
                `/user/signup?email=${encodeURIComponent(
                  formEmail,
                )}&referral=login`,
              )}
              onSocial={() => setSocial(true)}
              disabled={showForm}
            />

            {showForm && <FormPassword email={email} />}
            <Spacer spacing={1} />

            {/* Social Networks */}
            {!showForm && (
              <Grid container alignItems="center">
                <Grid item container sm={12} md={12} justify="center">
                  <Typography variant="base2" color="primary">
                    {t('login.social_networks_login')}
                  </Typography>
                </Grid>
                <Spacer spacing={4} />
                <OAuthLogin />
              </Grid>
            )}

            {/* bottom content */}
            <Divider className={classes.divider} />
            <Grid item xs={12}>
              {/* Reset pass/new account */}
              <div className={classes.forgotPasswordWrapper}>
                <Link
                  href={
                    email
                      ? `/user/password-reset?email=${encodeURIComponent(
                        email,
                      )}`
                      : '/user/password-reset'
                  }
                >
                  <Typography
                    variant="secondary2medium"
                    color="primary"
                    className={classes.link}
                  >
                    {t('login.actions.password_reset')}
                  </Typography>
                </Link>
                <Link href="/user/signup" styled>
                  <Typography
                    variant="secondary2medium"
                    color="primary"
                    className={classes.link}
                  >
                    {t('login.actions.new_client')}
                  </Typography>
                </Link>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </RootLayout>
      <Footer />
    </>
  );
};

interface Params {
  locale: string;
}

export async function getStaticProps({ locale }: Params) {
  return {
    props: {
      global,
      ...(await serverSideTranslations(locale, [
        'common',
        'users',
        'community',
        'dashboard',
        'settings',
        'markets',
        'feed',
        'components',
        'notifications',
      ])),
    },
  };
}

export default withAuthTry(withQueryParams(LoginPage));
